import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Col,
  Container,
  Row,
  Card,
  Button,
  ButtonGroup,
} from "react-bootstrap";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import moment from "moment";
import ReactVortoxSpinner from "../common-components/ReactVortoxSpinner";
import CommonStructuredV2 from "../common-components/CommonStructuredV2";
import {
  formatDistance,
  getGpsStatusColor,
  getLocationStatusColor,
} from "./TrackingUtils";
import "../../styles/tracking-system/location-tracking.css";
import { useGetFetchNew } from "../../data/Queries";
import { trackingSystemLocationLogs } from "../../data/ApiEndpoints";

const LocationTrackingLogs = ({ userId, userInfoData, onCloseModal, date }) => {
  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);
  const [viewMode, setViewMode] = useState("table"); // "table" or "map"
  const [selectedLocation, setSelectedLocation] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const circleRef = useRef(null);

  const {
    data: locationLogsData,
    isLoading: locationLogsLoading,
    refetch: refetchLocationLogs,
  } = useGetFetchNew({
    token: userInfoData?.token,
    key: "location_logs",
    url: trackingSystemLocationLogs,
    filters: {
      user_id: userId,
      limit: pageLimit || 10,
      page: isPage + 1,
      start_date: date,
      end_date: date,
    },
  });

  const centerLat = locationLogsData?.data?.[0]?.gym_lat || 28.6139;
  const centerLong = locationLogsData?.data?.[0]?.gym_long || 77.209;

  useEffect(() => {
    refetchLocationLogs();
  }, [date, userId, pageLimit, isPage, refetchLocationLogs]);

  // Cleanup circle on unmount
  useEffect(() => {
    return () => {
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, []);

  // Add markers to map
  const addMarkersToMap = useCallback((map, locations) => {
    // Clear existing markers
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    if (!locations || locations.length === 0) return;

    const bounds = new window.google.maps.LatLngBounds();
    const validLocations = locations.filter(
      (loc) => loc.user_lat && loc.user_long
    );

    validLocations.forEach((location, index) => {
      const position = {
        lat: parseFloat(location.user_lat),
        lng: parseFloat(location.user_long),
      };

      // Create custom marker icon based on status
      const statusColors = getLocationStatusColor(location.status);
      const isInRange =
        location.status === "in_range" || location.status === "within_range";

      const marker = new window.google.maps.Marker({
        position,
        map,
        title: `${location.name} - ${moment(location.date_added).format(
          "hh:mm A"
        )}`,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: statusColors.bg,
          fillOpacity: 0.9,
          strokeColor: isInRange ? "#28a745" : "#dc3545",
          strokeWeight: 2,
        },
        animation: null, // Remove bouncing animation
      });

      // Create info window
      const infoWindow = new window.google.maps.InfoWindow({
        content: createInfoWindowContent(location),
      });

      marker.addListener("click", () => {
        // Close other info windows
        markersRef.current.forEach((m) => {
          if (m.infoWindow) m.infoWindow.close();
        });

        infoWindow.open(map, marker);
        setSelectedLocation(location);
      });

      marker.infoWindow = infoWindow;
      markersRef.current.push(marker);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (validLocations.length > 1) {
      map.fitBounds(bounds);
    } else if (validLocations.length === 1) {
      map.setCenter({
        lat: parseFloat(validLocations[0].user_lat),
        lng: parseFloat(validLocations[0].user_long),
      });
      map.setZoom(16);
    }
  }, []);

  // Add circle overlay to map
  const addCircleToMap = useCallback((map, centerLat, centerLong) => {
    // Remove existing circle if any
    if (circleRef.current) {
      circleRef.current.setMap(null);
    }

    // Validate coordinates
    if (!centerLat || !centerLong || !map) {
      console.warn("Invalid coordinates or map for circle overlay:", {
        centerLat,
        centerLong,
        map,
      });
      return;
    }

    console.log("Creating circle with coordinates:", { centerLat, centerLong });

    try {
      const circle = new window.google.maps.Circle({
        strokeColor: "#28a745",
        strokeOpacity: 1.0,
        strokeWeight: 3,
        fillColor: "#28a745",
        fillOpacity: 0.2,
        map: map,
        center: { lat: parseFloat(centerLat), lng: parseFloat(centerLong) },
        radius: 150, // 150 meters radius
        clickable: false,
        zIndex: 1,
      });

      circleRef.current = circle;
      console.log("Circle created successfully:", circle);
    } catch (error) {
      console.error("Error creating circle:", error);
    }
  }, []);

  // Initialize Google Maps
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 4,
      center: { lat: centerLat, lng: centerLong }, // Default to Delhi
      mapTypeId: window.google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: "all",
          elementType: "geometry",
          stylers: [{ color: "#242f3e" }],
        },
        {
          featureType: "all",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#242f3e" }],
        },
        {
          featureType: "all",
          elementType: "labels.text.fill",
          stylers: [{ color: "#746855" }],
        },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#1b1b1b" }],
        },
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [{ color: "#263c3f" }],
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.fill",
          stylers: [{ color: "#6b9a76" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#38414e" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#212a37" }],
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [{ color: "#9ca5b3" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#746855" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1f2835" }],
        },
        {
          featureType: "road.highway",
          elementType: "labels.text.fill",
          stylers: [{ color: "#f3d19c" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#2f3948" }],
        },
        {
          featureType: "transit.station",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#17263c" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [{ color: "#515c6d" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#17263c" }],
        },
      ],
    });

    mapInstanceRef.current = map;

    // Add circle overlay with default coordinates if no data available
    const circleLat = centerLat; // Default to Delhi
    const circleLong = centerLong;
    addCircleToMap(map, circleLat, circleLong);

    // Add markers for location logs
    if (locationLogsData?.data?.length > 0) {
      addMarkersToMap(map, locationLogsData.data);
    }
  }, [
    locationLogsData,
    addMarkersToMap,
    addCircleToMap,
    centerLat,
    centerLong,
  ]);

  // Create info window content
  const createInfoWindowContent = (location) => {
    const statusColors = getLocationStatusColor(location.status);
    return `
      <div style="padding: 8px; min-width: 200px;">
        <h6 style="margin: 0 0 8px 0; color: #333; font-weight: 600;">${
          location.name
        }</h6>
        <div style="margin-bottom: 6px;">
          <span style="font-size: 12px; color: #666;">Time:</span>
          <strong style="margin-left: 4px; font-size: 13px;">${moment(
            location.date_added
          ).format("DD-MM-YYYY hh:mm A")}</strong>
        </div>
        <div style="margin-bottom: 6px;">
          <span style="font-size: 12px; color: #666;">Status:</span>
          <span style="
            margin-left: 4px; 
            padding: 2px 8px; 
            border-radius: 12px; 
            font-size: 11px; 
            font-weight: 600;
            background: ${statusColors.bg};
            color: ${statusColors.color};
            text-transform: capitalize;
          ">${location.status?.replace("_", " ") || "Unknown"}</span>
        </div>
        <div style="margin-bottom: 6px;">
          <span style="font-size: 12px; color: #666;">Distance:</span>
          <strong style="margin-left: 4px; font-size: 13px; color: ${
            location.status === "in_range" || location.status === "within_range"
              ? "#28a745"
              : "#dc3545"
          };">${formatDistance(location.distance_m)}</strong>
        </div>
        <div style="font-size: 11px; color: #888; margin-top: 8px;">
          Lat: ${location.user_lat}, Long: ${location.user_long}
        </div>
      </div>
    `;
  };

  // Load Google Maps script
  useEffect(() => {
    if (viewMode === "map" && !window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = initializeMap;
      document.head.appendChild(script);

      return () => {
        document.head.removeChild(script);
      };
    } else if (viewMode === "map" && window.google) {
      setTimeout(initializeMap, 100);
    }
  }, [viewMode, initializeMap]);

  // Update markers and circle when data changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      if (locationLogsData?.data) {
        addMarkersToMap(mapInstanceRef.current, locationLogsData.data);
      }

      // Always update circle with current center coordinates
      const circleLat = centerLat || 28.6139; // Default to Delhi
      const circleLong = centerLong || 77.209;
      addCircleToMap(mapInstanceRef.current, circleLat, circleLong);
    }
  }, [
    locationLogsData,
    addMarkersToMap,
    addCircleToMap,
    centerLat,
    centerLong,
  ]);

  const locationLogsColumns = [
    {
      dataField: "name",
      text: <p className="table__header">Employee</p>,
      formatter: (cell, row) => {
        return (
          <div className="employee-cell align-items-center">
            <div className="employee-info align-items-center">
              <p className="tb-data font-weight-bold">{row?.name || "-"}</p>
              <small className="tb-data text-muted">{row?.mobile || ""}</small>
              <span className="gym-badge">{row?.gym_name || ""}</span>
            </div>
            <span className="account-type-badge">
              {row?.account_type?.toUpperCase() || ""}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "location_info",
      text: <p className="table__header">Location Info</p>,
      formatter: (cell, row) => {
        return (
          <div className="location-info-cell align-items-center">
            <div className="coordinates">
              <span className="coordinate-label">Lat:</span>
              <span className="coordinate-value">{row?.user_lat || "-"}</span>
            </div>
            <div className="coordinates">
              <span className="coordinate-label">Long:</span>
              <span className="coordinate-value">{row?.user_long || "-"}</span>
            </div>
            <div className="distance-info">
              <span className="distance-label">Distance:</span>
              <span className="distance-value">
                {formatDistance(row?.distance_m)}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      dataField: "gps_status",
      text: <p className="table__header">GPS Status</p>,
      formatter: (cell, row) => {
        const gpsColors = getGpsStatusColor(row?.gps_status);
        const isEnabled = row?.gps_status === "1" || row?.gps_status === 1;
        return (
          <span
            className="tb-data status-badge"
            style={{
              background: gpsColors.bg,
              color: gpsColors.color,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            {isEnabled ? "Enabled" : "Disabled"}
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: <p className="table__header">Location Status</p>,
      formatter: (cell, row) => {
        const statusColors = getLocationStatusColor(row?.status);
        return (
          <span
            className="tb-data status-badge"
            style={{
              background: statusColors.bg,
              color: statusColors.color,
              padding: "6px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "600",
              textTransform: "capitalize",
            }}
          >
            {row?.status?.replace("_", " ") || "Unknown"}
          </span>
        );
      },
    },

    {
      dataField: "date_added",
      text: <p className="table__header">Logged At</p>,
      formatter: (cell, row) => {
        return (
          <div className="date-cell">
            <p className="tb-data table__Text">
              {row?.date_added
                ? moment(row.date_added).format("DD-MM-YYYY")
                : "-"}
            </p>
            <small className="time-display">
              {row?.date_added ? moment(row.date_added).format("hh:mm A") : ""}
            </small>
          </div>
        );
      },
    },
    {
      dataField: "action",
      text: <p className="table__header">Action</p>,
      formatter: (cell, row) => {
        return (
          <div className="actions-cell">
            <button
              className="logs-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                setViewMode("map");
                setSelectedLocation(row);
                setTimeout(() => {
                  if (mapInstanceRef.current && row.user_lat && row.user_long) {
                    const position = {
                      lat: parseFloat(row.user_lat),
                      lng: parseFloat(row.user_long),
                    };
                    mapInstanceRef.current.setCenter(position);
                    mapInstanceRef.current.setZoom(18);

                    // Find and trigger the marker click
                    const marker = markersRef.current.find(
                      (m) =>
                        m.getPosition().lat() === position.lat &&
                        m.getPosition().lng() === position.lng
                    );
                    if (marker && marker.infoWindow) {
                      marker.infoWindow.open(mapInstanceRef.current, marker);
                    }
                  }
                }, 500);
              }}
              title="View on Map"
            >
              <span
                style={{
                  display: "inline-block",
                  width: "15px",
                  height: "15px",
                  marginRight: "4px",
                }}
              >
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M10 2C7.243 2 5 4.243 5 7c0 5 5 11 5 11s5-6 5-11c0-2.757-2.243-5-5-5z"
                    stroke="#0e7490"
                    strokeWidth="1.5"
                    fill="#e0f2fe"
                  />
                  <circle cx="10" cy="7" r="2" fill="#0e7490" />
                </svg>
              </span>
              Map View
            </button>
          </div>
        );
      },
    },
    // {
    //   dataField: "day_timeline",
    //   text: <p className="table__header">Day Timeline</p>,
    //   formatter: (cell, row) => {
    //     return (
    //       <div
    //         className="underline"
    //         onClick={(e) => {
    //           e.stopPropagation();
    //           // handleViewBreakDetails(row);
    //         }}
    //       >
    //         <span className="timeline-icon">📊</span>
    //         <span>Click & View</span>
    //       </div>
    //     );
    //   },
    // },
  ];

  return (
    <div
      className="createNewCTOModalContainer mod-width-90p"
      style={{ height: "95vh" }}
    >
      {/* <div className="location-tracking-container"> */}
      <Container className="bg-cloud-k gobal-container" fluid>
        <Row>
          <Col>
            <CommonStructuredV2
              pageHeading={"Location Tracking Logs"}
              subHeading={
                "Monitor employee location tracking and GPS activity logs"
              }
              isReload={() => refetchLocationLogs()}
              onCloseModal={onCloseModal}
            />
          </Col>
        </Row>

        {/* View Mode Toggle */}
        <Row className="mb-3">
          <Col>
            <Card className="border-0 shadow-sm">
              <Card.Body className="py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      📍 Location Tracking for{" "}
                      {locationLogsData?.data?.[0]?.name || "Employee"}
                    </h6>
                    <small className="text-muted">
                      {moment(date).format("DD MMMM YYYY")} •{" "}
                      {locationLogsData?.data?.length || 0} locations logged
                    </small>
                  </div>
                  <ButtonGroup size="sm">
                    <Button
                      variant={
                        viewMode === "table" ? "primary" : "outline-primary"
                      }
                      onClick={() => setViewMode("table")}
                      className="d-flex align-items-center gap-2"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                      Table View
                    </Button>
                    <Button
                      variant={
                        viewMode === "map" ? "primary" : "outline-primary"
                      }
                      onClick={() => setViewMode("map")}
                      className="d-flex align-items-center gap-2"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M10 2C7.243 2 5 4.243 5 7c0 5 5 11 5 11s5-6 5-11c0-2.757-2.243-5-5-5z" />
                        <circle cx="10" cy="7" r="2" />
                      </svg>
                      Map View
                    </Button>
                  </ButtonGroup>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Content Area */}
        {viewMode === "table" ? (
          <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table location-tracking-table-container">
            {locationLogsLoading ? (
              <ReactVortoxSpinner />
            ) : (
              <PaymentLinkTable
                tableColumns={locationLogsColumns}
                tableData={locationLogsData?.data || []}
                tablePagination={locationLogsData?.pagination}
                isPage={isPage}
                setIsPage={(value) => setIsPage(value)}
                setPageLimit={setPageLimit}
                pageLimit={pageLimit}
                isFirstColSticky={true}
              />
            )}
          </div>
        ) : (
          <Row>
            {/* Left Side - Location Logs List */}
            <Col md={4}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">📍 Location Logs</h6>
                  <br />
                  <small className="text-muted">
                    Click any log to view on map
                  </small>
                </Card.Header>
                <Card.Body className="p-0">
                  {locationLogsLoading ? (
                    <div className="logs-loading-container">
                      <ReactVortoxSpinner />
                      <p className="mt-3 text-muted text-center">
                        Loading location logs...
                      </p>
                    </div>
                  ) : locationLogsData?.data?.length === 0 ? (
                    <div className="logs-empty-state">
                      <div className="empty-icon">📍</div>
                      <h6>No Location Logs</h6>
                      <p>No location data found for this date.</p>
                    </div>
                  ) : (
                    <div className="location-logs-list">
                      {locationLogsData?.data?.map((log, index) => (
                        <div
                          key={index}
                          className={`location-log-item ${
                            selectedLocation?.date_added === log?.date_added
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => {
                            setSelectedLocation(log);
                            if (
                              mapInstanceRef.current &&
                              log.user_lat &&
                              log.user_long
                            ) {
                              const position = {
                                lat: parseFloat(log.user_lat),
                                lng: parseFloat(log.user_long),
                              };
                              mapInstanceRef.current.setCenter(position);
                              mapInstanceRef.current.setZoom(17);

                              // Find and open the marker info window
                              const marker = markersRef.current.find(
                                (m) =>
                                  Math.abs(
                                    m.getPosition().lat() - position.lat
                                  ) < 0.0001 &&
                                  Math.abs(
                                    m.getPosition().lng() - position.lng
                                  ) < 0.0001
                              );
                              if (marker && marker.infoWindow) {
                                // Close all other info windows first
                                markersRef.current.forEach((m) => {
                                  if (m.infoWindow) m.infoWindow.close();
                                });
                                marker.infoWindow.open(
                                  mapInstanceRef.current,
                                  marker
                                );
                              }
                            }
                          }}
                        >
                          <div className="log-header">
                            <div className="log-time">
                              <strong>
                                {moment(log.date_added).format("hh:mm A")}
                              </strong>
                            </div>
                            <div
                              className={`log-status-indicator ${log.status}`}
                            >
                              <span
                                className={`status-dot ${
                                  log.status === "in_range" ||
                                  log.status === "within_range"
                                    ? "in-range"
                                    : "out-of-range"
                                }`}
                              ></span>
                              {log.status === "in_range" ||
                              log.status === "within_range"
                                ? "In Range"
                                : "Out of Range"}
                            </div>
                          </div>
                          <div className="log-details">
                            <div className="log-distance">
                              📏 {formatDistance(log.distance_m)}
                            </div>
                            <div className="log-coordinates">
                              📍 {parseFloat(log.user_lat).toFixed(4)},{" "}
                              {parseFloat(log.user_long).toFixed(4)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Right Side - Map */}
            <Col md={8}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Header className="bg-light">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-0">🗺️ Location Map</h6>
                      <small className="text-muted">
                        {selectedLocation
                          ? `Showing: ${moment(
                              selectedLocation.date_added
                            ).format("hh:mm A")}`
                          : "Select a log entry to focus on map"}
                      </small>
                    </div>
                    {/* <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        if (mapInstanceRef.current) {
                          const circleLat = centerLat || 28.6139;
                          const circleLong = centerLong || 77.209;
                          console.log("Manually adding circle at:", {
                            circleLat,
                            circleLong,
                          });
                          addCircleToMap(
                            mapInstanceRef.current,
                            circleLat,
                            circleLong
                          );
                        }
                      }}
                      title="Test Circle Creation"
                    >
                      Test Circle
                    </button> */}
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="location-map-container">
                    {locationLogsLoading ? (
                      <div className="map-loading-container">
                        <ReactVortoxSpinner />
                        <p className="mt-3 text-muted">Loading map...</p>
                      </div>
                    ) : locationLogsData?.data?.length === 0 ? (
                      <div className="map-empty-state">
                        <div className="empty-icon">🗺️</div>
                        <h6>No Map Data</h6>
                        <p>No location data to display on map.</p>
                      </div>
                    ) : (
                      <>
                        <div
                          ref={mapRef}
                          className="google-map"
                          style={{ height: "90%", width: "100%" }}
                        />

                        {/* Map Legend */}
                        <div className="map-legend">
                          <div className="legend-title">Legend</div>
                          <div className="legend-item">
                            <div className="legend-marker in-range"></div>
                            <span>In Range</span>
                          </div>
                          <div className="legend-item">
                            <div className="legend-marker out-of-range"></div>
                            <span>Out of Range</span>
                          </div>
                          <div className="legend-item">
                            <div className="legend-marker unknown"></div>
                            <span>Unknown</span>
                          </div>
                          {/* <div className="legend-item">
                            <div className="legend-circle"></div>
                            <span>150m Radius</span>
                          </div> */}
                        </div>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {viewMode === "table" &&
          locationLogsData?.data?.length === 0 &&
          !locationLogsLoading && (
            <div className="location-tracking-empty-state">
              <div className="empty-icon">📍</div>
              <h5>No Location Logs Found</h5>
              <p>No location tracking data found for the selected criteria.</p>
            </div>
          )}
      </Container>
    </div>
  );
};

export default LocationTrackingLogs;
